import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { Camera, X, Upload, AlertCircle, ImageIcon } from 'lucide-react';

interface GarageImageUploadProps {
    logo: File | null;
    garagePhotos: File[];
    onLogoChange: (file: File | null) => void;
    onPhotosChange: (files: File[]) => void;
    logoError?: string;
    photosError?: string;
}

const MAX_PHOTOS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export const GarageImageUpload: React.FC<GarageImageUploadProps> = ({
    logo,
    garagePhotos,
    onLogoChange,
    onPhotosChange,
    logoError,
    photosError
}) => {
    const { t } = useTranslation();
    const { isRTL } = useLanguage();
    const logoInputRef = useRef<HTMLInputElement>(null);
    const photosInputRef = useRef<HTMLInputElement>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
    const [dragOver, setDragOver] = useState<'logo' | 'photos' | null>(null);

    const validateFile = (file: File): string | null => {
        if (!ACCEPTED_TYPES.includes(file.type)) return t('register.upload.invalidType');
        if (file.size > MAX_FILE_SIZE) return t('register.upload.fileTooLarge');
        return null;
    };

    const handleLogoSelect = (file: File) => {
        const error = validateFile(file);
        if (error) { alert(error); return; }
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target?.result as string);
        reader.readAsDataURL(file);
        onLogoChange(file);
    };

    const handleLogoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleLogoSelect(file);
    };

    const handleRemoveLogo = () => {
        setLogoPreview(null);
        onLogoChange(null);
        if (logoInputRef.current) logoInputRef.current.value = '';
    };

    const handlePhotosSelect = (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const remainingSlots = MAX_PHOTOS - garagePhotos.length;
        if (fileArray.length > remainingSlots) {
            alert(t('register.upload.maxPhotosReached', { max: MAX_PHOTOS, remaining: remainingSlots }));
            return;
        }
        const validFiles: File[] = [];
        const newPreviews: string[] = [];
        fileArray.forEach(file => {
            const error = validateFile(file);
            if (error) { alert(`${file.name}: ${error}`); return; }
            validFiles.push(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                newPreviews.push(e.target?.result as string);
                if (newPreviews.length === validFiles.length) {
                    setPhotoPreviews(prev => [...prev, ...newPreviews]);
                }
            };
            reader.readAsDataURL(file);
        });
        if (validFiles.length > 0) onPhotosChange([...garagePhotos, ...validFiles]);
    };

    const handlePhotosInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) handlePhotosSelect(e.target.files);
    };

    const handleRemovePhoto = (index: number) => {
        const newPhotos = [...garagePhotos];
        newPhotos.splice(index, 1);
        onPhotosChange(newPhotos);
        const newPreviews = [...photoPreviews];
        newPreviews.splice(index, 1);
        setPhotoPreviews(newPreviews);
    };

    const handleDragOver = (e: React.DragEvent, zone: 'logo' | 'photos') => {
        e.preventDefault();
        setDragOver(zone);
    };
    const handleDragLeave = () => setDragOver(null);

    const handleDropLogo = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(null);
        const file = e.dataTransfer.files[0];
        if (file) handleLogoSelect(file);
    };

    const handleDropPhotos = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(null);
        if (e.dataTransfer.files) handlePhotosSelect(e.dataTransfer.files);
    };

    return (
        <div className="space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>

            {/* ===== LOGO UPLOAD ===== */}
            <div>
                <label className={`flex items-center gap-1 text-sm font-bold text-indigo-900 mb-3 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    {t('register.upload.logoTitle')}
                    <span className="text-red-500">*</span>
                </label>

                {!logoPreview ? (
                    <div
                        onDragOver={(e) => handleDragOver(e, 'logo')}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDropLogo}
                        onClick={() => logoInputRef.current?.click()}
                        className={`
                            relative cursor-pointer group
                            border-2 border-dashed rounded-2xl
                            p-8 text-center
                            transition-all duration-300
                            ${dragOver === 'logo'
                                ? 'border-indigo-500 bg-indigo-50 scale-[1.01]'
                                : logoError
                                    ? 'border-red-400 bg-red-50 hover:border-red-500'
                                    : 'border-indigo-300 bg-slate-50 hover:border-indigo-500 hover:bg-indigo-50'
                            }
                        `}
                    >
                        <div className="flex flex-col items-center gap-3">
                            <div className={`
                                w-16 h-16 rounded-2xl flex items-center justify-center
                                transition-all duration-300 group-hover:scale-110
                                ${logoError
                                    ? 'bg-red-100'
                                    : 'bg-indigo-100'
                                }
                            `}>
                                <Camera className={`w-8 h-8 ${logoError ? 'text-red-500' : 'text-indigo-600'}`} />
                            </div>
                            <div>
                                <p className="text-sm font-bold text-indigo-700">
                                    {t('register.upload.logoDropText')}
                                </p>
                                <p className="text-xs text-indigo-400 mt-1">
                                    {t('register.upload.logoHint')}
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="relative inline-block group">
                        <div className="absolute -inset-1 bg-gradient-to-br from-indigo-400 to-indigo-700 rounded-3xl opacity-30 blur-sm group-hover:opacity-50 transition-opacity" />
                        <div className="relative w-32 h-32 rounded-2xl overflow-hidden border-2 border-indigo-400 shadow-lg">
                            <img
                                src={logoPreview}
                                alt="Logo preview"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors z-10"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleLogoInputChange}
                    className="hidden"
                />

                {logoError && (
                    <div className={`flex items-center gap-2 mt-2 text-sm text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{logoError}</span>
                    </div>
                )}
            </div>

            {/* ===== GARAGE PHOTOS (required, min 1, max 3) ===== */}
            <div>
                <label className={`flex items-center gap-1 text-sm font-bold text-indigo-900 mb-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                    {t('register.upload.photosTitle')}
                    <span className="text-red-500">*</span>
                </label>
                <p className={`text-xs text-indigo-400 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('register.upload.photosHint', { max: MAX_PHOTOS })}
                </p>

                {/* Photos grid */}
                <div className="grid grid-cols-3 gap-4">
                    {/* Existing photos */}
                    {photoPreviews.map((preview, index) => (
                        <div key={index} className="relative aspect-square group">
                            <div className="absolute -inset-0.5 bg-gradient-to-br from-indigo-400 to-indigo-700 rounded-2xl opacity-0 group-hover:opacity-30 transition-opacity blur-sm" />
                            <div className="relative w-full h-full rounded-2xl overflow-hidden border-2 border-indigo-200 shadow-md group-hover:border-indigo-400 transition-colors">
                                <img
                                    src={preview}
                                    alt={`Garage photo ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-indigo-900/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemovePhoto(index)}
                                className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors z-10"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}

                    {/* Empty slots */}
                    {Array.from({ length: Math.max(0, MAX_PHOTOS - garagePhotos.length) }).map((_, i) => {
                        const showAdd = i === 0;
                        const isFirstSlot = i === 0;
                        return (
                            <div
                                key={`slot-${i}`}
                                onDragOver={showAdd ? (e) => handleDragOver(e, 'photos') : undefined}
                                onDragLeave={showAdd ? handleDragLeave : undefined}
                                onDrop={showAdd ? handleDropPhotos : undefined}
                                onClick={showAdd ? () => photosInputRef.current?.click() : undefined}
                                className={`
                                    aspect-square rounded-2xl
                                    flex flex-col items-center justify-center gap-2
                                    transition-all duration-300
                                    ${showAdd ? 'cursor-pointer' : 'cursor-default opacity-40'}
                                    ${dragOver === 'photos' && showAdd
                                        ? 'border-2 border-indigo-500 bg-indigo-50 scale-[1.02]'
                                        : photosError && garagePhotos.length === 0 && isFirstSlot
                                            ? 'border-2 border-dashed border-red-400 bg-red-50 hover:border-red-500'
                                            : 'border-2 border-dashed border-indigo-200 bg-slate-50 hover:border-indigo-400 hover:bg-indigo-50'
                                    }
                                `}
                            >
                                {showAdd ? (
                                    <>
                                        <div className={`
                                            w-10 h-10 rounded-xl flex items-center justify-center
                                            transition-all duration-300
                                            ${photosError && garagePhotos.length === 0 ? 'bg-red-100' : 'bg-indigo-100'}
                                        `}>
                                            <Upload className={`w-5 h-5 ${photosError && garagePhotos.length === 0 ? 'text-red-500' : 'text-indigo-600'}`} />
                                        </div>
                                        <span className="text-xs font-semibold text-indigo-500 text-center px-2 leading-tight">
                                            {t('register.upload.addPhoto')}
                                        </span>
                                        <span className={`
                                            text-xs font-bold px-2 py-0.5 rounded-full
                                            ${garagePhotos.length === 0
                                                ? 'bg-indigo-100 text-indigo-700'
                                                : 'bg-indigo-50 text-indigo-400'
                                            }
                                        `}>
                                            {garagePhotos.length}/{MAX_PHOTOS}
                                        </span>
                                    </>
                                ) : (
                                    <ImageIcon className="w-6 h-6 text-indigo-200" />
                                )}
                            </div>
                        );
                    })}
                </div>

                <input
                    ref={photosInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    multiple
                    onChange={handlePhotosInputChange}
                    className="hidden"
                />

                {/* Min required indicator */}
                {garagePhotos.length === 0 && !photosError && (
                    <div className={`flex items-center gap-2 mt-3 text-xs text-indigo-400 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                        <span>{t('register.upload.photosMinHint', { min: 1 })}</span>
                    </div>
                )}

                {photosError && (
                    <div className={`flex items-center gap-2 mt-2 text-sm text-red-600 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{photosError}</span>
                    </div>
                )}

                {/* Progress bar */}
                {garagePhotos.length > 0 && (
                    <div className="mt-3">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-semibold text-indigo-500">
                                {garagePhotos.length}/{MAX_PHOTOS} {t('register.upload.photosAdded')}
                            </span>
                        </div>
                        <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-indigo-400 to-indigo-700 rounded-full transition-all duration-500"
                                style={{ width: `${(garagePhotos.length / MAX_PHOTOS) * 100}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GarageImageUpload;
